// ============================================================
// CardPresence.tsx
// "Who is editing THIS card" — an avatar stack + "Priya is editing" shown in
// a section card's HEADER, so a colleague's presence is visible while you're
// only VIEWING the card (the per-field FormField indicator only exists once a
// card is open for editing, because only then are its fields mounted).
//
// Reads the SAME presence data FormField uses (useFieldOwners — the room's
// field owners, FormField.tsx:100-106): anyone whose focused fieldKey matches
// this card is shown. Display-only; never touches values or saves.
// ============================================================
import React from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { useFieldOwners } from '../../hooks/usePresence'
import type { PresenceFieldOwner } from '../../services/presence'
import PresenceAvatar from './PresenceAvatar'

// Remote owners (not me) whose fieldKey matches, one entry per person.
export const useCardEditors = (match: (fieldKey: string) => boolean): PresenceFieldOwner[] => {
  const owners = useFieldOwners()
  const selfId = useCasevoStore((s) => s.authUser?.id)
  const seen = new Set<string>()
  const out: PresenceFieldOwner[] = []
  for (const o of owners) {
    if (o.userId === selfId || seen.has(o.userId) || !match(o.fieldKey)) continue
    seen.add(o.userId)
    out.push(o)
  }
  return out
}

const firstName = (name: string): string => name.trim().split(/\s+/)[0] || name

export const editingLabel = (editors: PresenceFieldOwner[]): string => {
  const n = editors.map((e) => firstName(e.name))
  if (n.length === 0) return ''
  if (n.length === 1) return `${n[0]} is editing`
  if (n.length === 2) return `${n[0]} and ${n[1]} are editing`
  return `${n[0]} and ${n.length - 1} others are editing`
}

// Header chip: overlapping 20px presence avatars (ring = card surface) + label.
const CardPresence: React.FC<{ match: (fieldKey: string) => boolean; className?: string }> = ({ match, className = '' }) => {
  const editors = useCardEditors(match)
  if (editors.length === 0) return null
  const label = editingLabel(editors)
  return (
    <span role="status" aria-live="polite" className={`inline-flex items-center gap-1.5 text-[12px] font-semibold text-casevo-text-soft whitespace-nowrap ${className}`} title={label}>
      <span className="inline-flex items-center -space-x-1.5">
        {editors.slice(0, 3).map((o) => (
          <span key={o.userId} className="rounded-full ring-2 ring-casevo-surface inline-flex">
            <PresenceAvatar userId={o.userId} name={o.name} colour={o.avatarColour} photoUrl={o.avatarPhotoUrl} title={`${o.name} is editing`} />
          </span>
        ))}
      </span>
      <span className="hidden sm:inline">{label}</span>
    </span>
  )
}
export default CardPresence
